'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import { TableRowSkeleton } from '@/components/ui/LoadingSkeleton';

// Backend integration point: fetch from /api/centres?page=&limit=&zone=&status=&search=
const centresData = [
  { id: 'centre-001', name: 'Pune Shivajinagar BK Centre', zone: 'Maharashtra', coordinator: 'Br. Rajesh Kulkarni', trees: 1840, sakash: 124, awareness: 3200, cleanups: 4, champions: 3, lastSubmitted: '02 May 2026', status: 'submitted' as const, workshops: 2 },
  { id: 'centre-002', name: 'Mumbai Andheri BK Centre', zone: 'Maharashtra', coordinator: 'Sr. Anita Mehta', trees: 2210, sakash: 138, awareness: 4800, cleanups: 5, champions: 3, lastSubmitted: '01 May 2026', status: 'verified' as const, workshops: 3 },
  { id: 'centre-003', name: 'Nashik Road BK Centre', zone: 'Maharashtra', coordinator: 'Br. Sunil Pawar', trees: 980, sakash: 92, awareness: 1900, cleanups: 2, champions: 2, lastSubmitted: '28 Apr 2026', status: 'submitted' as const, workshops: 1 },
  { id: 'centre-004', name: 'Ahmedabad Maninagar Centre', zone: 'Gujarat', coordinator: 'Sr. Hemlata Patel', trees: 1560, sakash: 108, awareness: 2700, cleanups: 3, champions: 3, lastSubmitted: '30 Apr 2026', status: 'submitted' as const, workshops: 2 },
  { id: 'centre-005', name: 'Surat Adajan BK Centre', zone: 'Gujarat', coordinator: 'Br. Dinesh Shah', trees: 740, sakash: 61, awareness: 1100, cleanups: 1, champions: 1, lastSubmitted: '—', status: 'pending' as const, workshops: 0 },
  { id: 'centre-006', name: 'Jaipur Malviya Nagar Centre', zone: 'Rajasthan', coordinator: 'Sr. Pushpa Sharma', trees: 890, sakash: 78, awareness: 1650, cleanups: 2, champions: 2, lastSubmitted: '29 Apr 2026', status: 'submitted' as const, workshops: 1 },
  { id: 'centre-007', name: 'Jodhpur BK Centre', zone: 'Rajasthan', coordinator: 'Br. Vikram Singh', trees: 420, sakash: 44, awareness: 820, cleanups: 1, champions: 0, lastSubmitted: '—', status: 'pending' as const, workshops: 0 },
  { id: 'centre-008', name: 'Delhi Janakpuri BK Centre', zone: 'Delhi NCR', coordinator: 'Sr. Neelam Gupta', trees: 1320, sakash: 115, awareness: 3100, cleanups: 4, champions: 3, lastSubmitted: '03 May 2026', status: 'submitted' as const, workshops: 2 },
  { id: 'centre-009', name: 'Delhi Rohini BK Centre', zone: 'Delhi NCR', coordinator: 'Br. Anil Verma', trees: 680, sakash: 55, awareness: 1200, cleanups: 1, champions: 1, lastSubmitted: '—', status: 'draft' as const, workshops: 0 },
  { id: 'centre-010', name: 'Nagpur Dharampeth Centre', zone: 'Maharashtra', coordinator: 'Sr. Vandana Bhosale', trees: 1350, sakash: 99, awareness: 2400, cleanups: 3, champions: 3, lastSubmitted: '01 May 2026', status: 'verified' as const, workshops: 2 },
  { id: 'centre-011', name: 'Chennai T. Nagar BK Centre', zone: 'Tamil Nadu', coordinator: 'Sr. Kavitha Subramaniam', trees: 960, sakash: 87, awareness: 1980, cleanups: 2, champions: 3, lastSubmitted: '30 Apr 2026', status: 'submitted' as const, workshops: 1 },
  { id: 'centre-012', name: 'Bangalore Koramangala Centre', zone: 'Karnataka', coordinator: 'Br. Krishnaswamy', trees: 1120, sakash: 103, awareness: 2600, cleanups: 3, champions: 2, lastSubmitted: '02 May 2026', status: 'submitted' as const, workshops: 2 },
];

type SortKey = 'name' | 'trees' | 'sakash' | 'awareness' | 'champions' | 'lastSubmitted';

export default function CentreStatusTable() {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('trees');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [perPage] = useState(8);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading] = useState(false);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const filtered = centresData.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.zone.toLowerCase().includes(search.toLowerCase()) ||
      c.coordinator.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    if (typeof av === 'number' && typeof bv === 'number') {
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    return sortDir === 'asc'
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((page - 1) * perPage, page * perPage);

  const allSelected = paginated.every((c) => selectedIds.includes(c.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !paginated.map((c) => c.id).includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...paginated.map((c) => c.id)])]);
    }
  };
  const toggleRow = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <Icon name="ChevronUpDownIcon" size={12} variant="outline" className="text-muted-foreground/50" />;
    return sortDir === 'asc'
      ? <Icon name="ChevronUpIcon" size={12} variant="outline" className="text-primary" />
      : <Icon name="ChevronDownIcon" size={12} variant="outline" className="text-primary" />;
  };

  return (
    <div className="bg-card rounded-2xl border border-border shadow-card overflow-hidden">
      {/* Table header */}
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border flex-wrap">
        <div>
          <h2 className="text-base font-bold text-foreground">Centre Status</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{filtered.length} centres — April 2026</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Icon name="MagnifyingGlassIcon" size={15} variant="outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search centres, zones, coordinators..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 pr-3 py-2 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 w-64"
            />
          </div>
          <button className="btn-secondary text-xs px-3 py-2">
            <Icon name="ArrowDownTrayIcon" size={14} variant="outline" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 px-5 py-3 bg-primary/5 border-b border-primary/20 animate-slide-up">
          <span className="text-sm font-semibold text-primary">{selectedIds.length} selected</span>
          <button className="btn-ghost text-xs text-[var(--danger)] hover:bg-[var(--danger-bg)]">
            <Icon name="EnvelopeIcon" size={14} variant="outline" />
            Send Reminder
          </button>
          <button className="btn-ghost text-xs">
            <Icon name="ArrowDownTrayIcon" size={14} variant="outline" />
            Export Selected
          </button>
          <button
            onClick={() => setSelectedIds([])}
            className="ml-auto btn-ghost text-xs text-muted-foreground"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="rounded border-border accent-primary"
                  aria-label="Select all"
                />
              </th>
              {[
                { key: 'name' as SortKey, label: 'Centre / Zone' },
                { key: 'trees' as SortKey, label: 'Trees 🌳' },
                { key: 'sakash' as SortKey, label: 'Sakash' },
                { key: 'awareness' as SortKey, label: 'Awareness Reach' },
                { key: 'champions' as SortKey, label: 'Champions' },
              ].map((col) => (
                <th
                  key={`th-${col.key}`}
                  className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground select-none"
                  onClick={() => handleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    <SortIcon col={col.key} />
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Workshops</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground select-none" onClick={() => handleSort('lastSubmitted')}>
                <div className="flex items-center gap-1">
                  Last Submitted
                  <SortIcon col="lastSubmitted" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => <TableRowSkeleton key={`skel-row-${i}`} cols={9} />)
            ) : paginated.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <EmptyState
                    icon="BuildingOffice2Icon"
                    title="No centres found"
                    description="No centres match your current search or filter criteria. Try adjusting the filters above."
                  />
                </td>
              </tr>
            ) : (
              paginated.map((centre, rowIdx) => (
                <tr
                  key={centre.id}
                  className={`border-b border-border transition-colors hover:bg-muted/40 group ${rowIdx % 2 === 0 ? '' : 'bg-muted/10'} ${selectedIds.includes(centre.id) ? 'bg-primary/5' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(centre.id)}
                      onChange={() => toggleRow(centre.id)}
                      className="rounded border-border accent-primary"
                      aria-label={`Select ${centre.name}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-foreground leading-tight max-w-[180px] truncate" title={centre.name}>
                      {centre.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{centre.zone} · {centre.coordinator}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-bold text-primary tabular-nums">{centre.trees.toLocaleString('en-IN')}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold text-foreground tabular-nums">{centre.sakash}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold text-foreground tabular-nums">{centre.awareness.toLocaleString('en-IN')}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <span
                          key={`trophy-${centre.id}-${i}`}
                          className={`text-sm ${i < centre.champions ? 'opacity-100' : 'opacity-20'}`}
                        >
                          🏆
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold text-foreground tabular-nums">{centre.workshops}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={centre.status} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${centre.lastSubmitted === '—' ? 'text-[var(--danger)]' : 'text-muted-foreground'}`}>
                      {centre.lastSubmitted}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                        title="View centre details"
                        aria-label="View centre details"
                      >
                        <Icon name="EyeIcon" size={15} variant="outline" className="text-muted-foreground" />
                      </button>
                      <button
                        className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                        title="Send submission reminder"
                        aria-label="Send submission reminder"
                      >
                        <Icon name="EnvelopeIcon" size={15} variant="outline" className="text-muted-foreground" />
                      </button>
                      <button
                        className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                        title="Download centre report"
                        aria-label="Download centre report"
                      >
                        <Icon name="ArrowDownTrayIcon" size={15} variant="outline" className="text-muted-foreground" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-border flex-wrap gap-2">
        <p className="text-xs text-muted-foreground">
          Showing {Math.min((page - 1) * perPage + 1, sorted.length)}–{Math.min(page * perPage, sorted.length)} of {sorted.length} centres
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <Icon name="ChevronLeftIcon" size={16} variant="outline" />
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={`page-${i + 1}`}
              onClick={() => setPage(i + 1)}
              className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${page === i + 1 ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-muted-foreground'}`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <Icon name="ChevronRightIcon" size={16} variant="outline" />
          </button>
        </div>
      </div>
    </div>
  );
}